import { ApiError } from "@/api";

export const MIN_STAKE_CENTS = 100;

export type BetSlipErrorPhase = "quote" | "place";
export type BetSlipErrorKind =
  | "closed"
  | "auth"
  | "capability"
  | "funds"
  | "validation"
  | "unavailable"
  | "unknown";

export type BetSlipErrorView = {
  code: string;
  kind: BetSlipErrorKind;
  message: string;
};

const ERROR_COPY: Record<string, Omit<BetSlipErrorView, "code">> = {
  RACE_LOCKED: {
    kind: "closed",
    message: "Betting has closed for this race.",
  },
  BETTING_NOT_OPEN_YET: {
    kind: "closed",
    message: "Betting is not open for this race yet.",
  },
  RACECARD_NOT_PUBLISHED: {
    kind: "closed",
    message: "Betting is not available for this race yet.",
  },
  SEED_NOT_HELD: {
    kind: "closed",
    message: "Betting is not available for this race.",
  },
  NOT_A_LIVE_RACE: {
    kind: "closed",
    message: "This race is not available for betting.",
  },
  RACE_NOT_FOUND: {
    kind: "closed",
    message: "This race is no longer available.",
  },
  CAPABILITY_BLOCKED_PLATFORM_BETTING: {
    kind: "capability",
    message: "Betting is not available in your region.",
  },
  SESSION_MISSING: {
    kind: "auth",
    message: "Sign in again to place your bet.",
  },
  PLAYER_AUTH_REQUIRED: {
    kind: "auth",
    message: "Sign in again to place your bet.",
  },
  PLAYER_NOT_FOUND: {
    kind: "auth",
    message: "Sign in again to place your bet.",
  },
  INSUFFICIENT_FUNDS: {
    kind: "funds",
    message: "You do not have enough available funds for this bet.",
  },
  STAKE_OUT_OF_BOUNDS: {
    kind: "validation",
    message: "Enter a stake within the allowed limits.",
  },
  BAD_COUPON: {
    kind: "validation",
    message: "Check your selections and try again.",
  },
  BAD_COUPON_TYPE: {
    kind: "validation",
    message: "Check your bet type and try again.",
  },
  BAD_BET_TYPE: {
    kind: "validation",
    message: "One of your selections is no longer available.",
  },
  NO_LEGS: {
    kind: "validation",
    message: "Add a selection before placing your bet.",
  },
  BET_DENIED: {
    kind: "validation",
    message: "This bet could not be accepted.",
  },
  BRIDGE_DOWN: {
    kind: "unavailable",
    message: "Betting is temporarily unavailable. Please try again.",
  },
  BET_BACKEND_UNAVAILABLE: {
    kind: "unavailable",
    message: "Betting is temporarily unavailable. Please try again.",
  },
  HTTP_429: {
    kind: "unavailable",
    message: "Please wait a moment before trying again.",
  },
  HTTP_503: {
    kind: "unavailable",
    message: "Betting is temporarily unavailable. Please try again.",
  },
};

export function presentBetSlipError(
  error: unknown,
  phase: BetSlipErrorPhase,
): BetSlipErrorView {
  return presentBetSlipCode(error instanceof ApiError ? error.code : "", phase);
}

export function presentBetSlipCode(
  value: string,
  phase: BetSlipErrorPhase,
): BetSlipErrorView {
  const code = safeErrorCode(value);
  const known = ERROR_COPY[code];
  if (known) return { code, ...known };
  return {
    code,
    kind: "unknown",
    message:
      phase === "quote"
        ? "We could not update this bet. Check your selections and try again."
        : "We could not place your bet. Please try again.",
  };
}

export function safeErrorCode(value: string): string {
  return /^[A-Z][A-Z0-9_]{1,63}$/.test(value) ? value : "UNKNOWN_ERROR";
}

export function stakeInputFromCents(cents: number): string {
  const safeCents = Number.isSafeInteger(cents) && cents >= 0 ? cents : 0;
  return `${Math.floor(safeCents / 100)}.${String(safeCents % 100).padStart(2, "0")}`;
}

export function parseStakeCents(value: string): number | null {
  const normalized = value.trim();
  if (!/^\d{0,12}(?:\.\d{0,2})?$/.test(normalized)) return null;
  if (!normalized || normalized === ".") return 0;
  const [whole = "0", fraction = ""] = normalized.split(".");
  const cents =
    Number.parseInt(whole || "0", 10) * 100 +
    Number.parseInt(fraction.padEnd(2, "0") || "0", 10);
  return Number.isSafeInteger(cents) ? cents : null;
}

export function displayRaceLabel(label: string): string {
  return label
    .replace(
      /\b[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}\b/gi,
      "",
    )
    .replace(/\s*·\s*R(?=[0-9a-f]{4}\b)(?=[0-9a-f]*[a-f])[0-9a-f]{4}\b/gi, "")
    .replace(/\s*·\s*$/, "")
    .trim();
}
